import toast from "react-hot-toast";

/* =========================================================
   NOTIFICATION PERMISSION
========================================================= */

export async function ensureNotificationPermission() {
  if (
    typeof window === "undefined" ||
    !("Notification" in window)
  ) {
    return "unsupported";
  }

  if (Notification.permission !== "default") {
    return Notification.permission;
  }

  try {
    return await Notification.requestPermission();
  } catch (err) {
    console.warn(
      "Notification permission request failed:",
      err
    );

    return Notification.permission;
  }
}

/* =========================================================
   CAN SHOW WINDOWS NOTIFICATION
========================================================= */

export function canNotify() {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    Notification.permission === "granted"
  );
}

/* =========================================================
   SOUND
========================================================= */

function playBeep() {
  try {
    const AudioCtx =
      window.AudioContext ||
      window.webkitAudioContext;

    if (!AudioCtx) return;

    const ctx = new AudioCtx();

    const oscillator =
      ctx.createOscillator();

    const gain =
      ctx.createGain();

    oscillator.connect(gain);
    gain.connect(ctx.destination);

    oscillator.frequency.value = 880;
    gain.gain.value = 0.04;

    oscillator.start();

    setTimeout(() => {
      try {
        oscillator.stop();
        ctx.close();
      } catch {}
    }, 140);
  } catch {}
}

/* =========================================================
   ERP FRONT SCREEN TOAST
========================================================= */

function showErpToast({
  title,
  body,
  icon,
  route,
  tag,
}) {
  const preview =
    String(body || "")
      .trim()
      .slice(0, 160);

  toast.custom(
    (t) => (
      <div
        onClick={() => {
          toast.dismiss(t.id);

          if (route) {
            window.location.assign(route);
          }
        }}
        style={{
          width: "360px",
          maxWidth: "calc(100vw - 30px)",

          display: "flex",
          alignItems: "flex-start",
          gap: "12px",

          padding: "14px",

          background: "#ffffff",

          border: "1px solid #e2e8f0",

          borderRadius: "12px",

          boxShadow:
            "0 12px 35px rgba(15, 23, 42, 0.22)",

          cursor: "pointer",

          opacity: t.visible
            ? 1
            : 0,

          transform: t.visible
            ? "translateY(0)"
            : "translateY(-10px)",

          transition:
            "all 0.2s ease",

          pointerEvents: "auto",
        }}
      >
        {/* ICON */}

        <div
          style={{
            width: "42px",
            height: "42px",

            borderRadius: "10px",

            overflow: "hidden",

            flexShrink: 0,

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            background: "#eef2ff",

            fontSize: "18px",
            fontWeight: 700,
          }}
        >
          {icon ? (
            <img
              src={icon}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              onError={(e) => {
                e.currentTarget.style.display =
                  "none";
              }}
            />
          ) : (
            "💬"
          )}
        </div>

        {/* CONTENT */}

        <div
          style={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <div
            style={{
              fontSize: "13px",
              color: "#64748b",
              marginBottom: "2px",
            }}
          >
            New message
          </div>

          <div
            style={{
              fontSize: "15px",
              fontWeight: 700,
              color: "#0f172a",

              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title || "New Message"}
          </div>

          <div
            style={{
              marginTop: "4px",

              fontSize: "13px",
              lineHeight: "18px",

              color: "#475569",

              overflow: "hidden",

              whiteSpace: "pre-line",

              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient:
                "vertical",
            }}
          >
            {preview ||
              "New message received"}
          </div>
        </div>

        {/* CLOSE */}

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();

            toast.dismiss(t.id);
          }}
          style={{
            border: 0,
            background: "transparent",

            fontSize: "20px",
            color: "#94a3b8",

            cursor: "pointer",

            padding: 0,

            width: "24px",
            height: "24px",
          }}
        >
          ×
        </button>
      </div>
    ),
    {
      id:
        tag ||
        `chat-${Date.now()}`,

      duration: 6000,

      position: "top-right",
    }
  );
}

/* =========================================================
   MAIN CHAT NOTIFICATION
========================================================= */

export function notifyChat({
  title = "New Message",

  body = "",

  tag = "chat",

  route = "/messenger",

  icon = "/sdc-notification.png",

  silent = false,
}) {
  if (
    typeof window === "undefined"
  ) {
    return null;
  }

  const preview =
    String(body || "")
      .trim()
      .slice(0, 180);

  /* =====================================================
     WINDOWS DESKTOP NOTIFICATION

     IMPORTANT:
     Show desktop popup even when ERP tab is visible.
  ===================================================== */

  if (canNotify()) {
    try {
      console.log(
        "🖥 Showing Windows desktop notification",
        {
          title,
          body: preview,
          visibility:
            document.visibilityState,
        }
      );

      /*
       * Unique tag ensures every incoming message
       * can create a fresh Windows popup.
       */
      const uniqueTag =
        `${tag || "chat"}:${Date.now()}`;

      const notification =
        new Notification(
          title,
          {
            body:
              preview ||
              "New message received",

            tag: uniqueTag,

            icon:
              icon ||
              "/sdc-notification.png",

            badge:
              "/sdc-notification.png",

            requireInteraction:
              false,

            silent,
          }
        );

      if (!silent) {
        playBeep();
      }

      notification.onclick =
        () => {
          try {
            window.focus();
          } catch {}

          try {
            if (route) {
              window.location.assign(
                route
              );
            }
          } catch {}

          try {
            notification.close();
          } catch {}
        };

      /*
       * When ERP itself is visible,
       * Windows popup still appears.
       *
       * We do NOT show another ERP toast here
       * to avoid duplicate notifications.
       */

      return notification;
    } catch (err) {
      console.warn(
        "Desktop notification failed:",
        err
      );
    }
  }

  /* =====================================================
     FALLBACK

     If desktop notifications are blocked/not available,
     show ERP toast.
  ===================================================== */

  console.log(
    "💬 Windows notification unavailable - showing ERP toast"
  );

  showErpToast({
    title,
    body: preview,
    icon,
    route,
    tag,
  });

  if (!silent) {
    playBeep();
  }

  return null;
}

/* =========================================================
   ROUTES / KEYS
========================================================= */

export const notif = {
  dmKey: (otherUserId) =>
    `dm:${otherUserId}`,

  projectKey: (projectId) =>
    `project:${projectId}`,

  announceKey: (company) =>
    `announce:${String(
      company || "SDC"
    ).toUpperCase()}`,

  dmRoute: () =>
    "/messenger",

  projectRoute: () =>
    "/messenger/project-communication",

  announceRoute: () =>
    "/messenger",
};